export const CHUNK_RECOVERY_GUARD_KEY = 'chunk-load-recovery:last-reload-at';
export const CHUNK_RECOVERY_GUARD_TTL_MS = 5 * 60 * 1000;
export const CHUNK_RECOVERY_FALLBACK_PATH = '/something-went-wrong';

interface RecoverFromChunkLoadFailureArgs {
  error: unknown;
  storage?: Pick<Storage, 'getItem' | 'setItem'>;
  location?: ChunkRecoveryLocation;
  now?: number;
}

type ChunkRecoveryStorage = Pick<Storage, 'getItem' | 'setItem'>;
type ChunkRecoveryLocation = Pick<Location, 'reload'> &
  Partial<Pick<Location, 'assign' | 'pathname'>>;
type ChunkRecoveryListenerTarget = Pick<Window, 'addEventListener'>;

const CHUNK_LOAD_FAILURE_PATTERNS = [
  /loading chunk [\w-]+ failed/i,
  /chunkloaderror/i,
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
];

let memoryRecoveryAt = Number.NaN;

/** Test-only helper to clear module state between cases. */
export function resetChunkLoadRecoveryForTests() {
  memoryRecoveryAt = Number.NaN;
}

function getErrorMessage(error: unknown): string {
  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    return error.message;
  }

  return '';
}

export function isChunkLoadFailure(error: unknown): boolean {
  const message = getErrorMessage(error);

  return CHUNK_LOAD_FAILURE_PATTERNS.some((pattern) => pattern.test(message));
}

function resolveStorage(
  providedStorage?: ChunkRecoveryStorage
): ChunkRecoveryStorage | undefined {
  if (providedStorage) {
    return providedStorage;
  }

  try {
    return globalThis.sessionStorage;
  } catch {
    return undefined;
  }
}

function readPreviousRecoveryAt(
  resolvedStorage: ChunkRecoveryStorage | undefined
): number {
  const candidates: number[] = [];

  if (Number.isFinite(memoryRecoveryAt)) {
    candidates.push(memoryRecoveryAt);
  }

  if (resolvedStorage) {
    try {
      const storedValue = Number(
        resolvedStorage.getItem(CHUNK_RECOVERY_GUARD_KEY)
      );

      if (Number.isFinite(storedValue)) {
        candidates.push(storedValue);
      }
    } catch {
      // Fall back to in-memory guard only.
    }
  }

  if (candidates.length === 0) {
    return Number.NaN;
  }

  return Math.max(...candidates);
}

function writeRecoveryAt(
  resolvedStorage: ChunkRecoveryStorage | undefined,
  now: number
) {
  memoryRecoveryAt = now;

  if (!resolvedStorage) {
    return;
  }

  try {
    resolvedStorage.setItem(CHUNK_RECOVERY_GUARD_KEY, String(now));
  } catch {
    // In-memory guard still prevents reload loops in this page session.
  }
}

export function recoverFromChunkLoadFailure({
  error,
  storage,
  location = globalThis.location,
  now = Date.now(),
}: RecoverFromChunkLoadFailureArgs): boolean {
  if (!isChunkLoadFailure(error)) {
    return false;
  }

  const resolvedStorage = resolveStorage(storage);
  const previousRecoveryAt = readPreviousRecoveryAt(resolvedStorage);

  const shouldBlockReload =
    Number.isFinite(previousRecoveryAt) &&
    now - previousRecoveryAt < CHUNK_RECOVERY_GUARD_TTL_MS;

  if (shouldBlockReload) {
    if (
      location.pathname !== CHUNK_RECOVERY_FALLBACK_PATH &&
      typeof location.assign === 'function'
    ) {
      location.assign(CHUNK_RECOVERY_FALLBACK_PATH);
    }

    return false;
  }

  writeRecoveryAt(resolvedStorage, now);
  location.reload();

  return true;
}

function getVitePreloadError(event: Event): unknown {
  if ('payload' in event) {
    return event.payload;
  }

  if (event instanceof CustomEvent) {
    return event.detail;
  }

  return event;
}

export function installChunkRecoveryListeners(
  targetWindow: ChunkRecoveryListenerTarget = window
): void {
  targetWindow.addEventListener(
    'error',
    (event) => {
      recoverFromChunkLoadFailure({
        error: event.error ?? event.message,
      });
    },
    true
  );

  targetWindow.addEventListener('unhandledrejection', (event) => {
    recoverFromChunkLoadFailure({
      error: event.reason,
    });
  });

  targetWindow.addEventListener('vite:preloadError', (event) => {
    const error = getVitePreloadError(event);

    if (!isChunkLoadFailure(error)) {
      return;
    }

    event.preventDefault();
    recoverFromChunkLoadFailure({ error });
  });
}
